import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Car, Link2 } from "lucide-react";
import { formatUSD } from "@/lib/format";

const IRS_RATE_2026 = 0.70;

const inputCls =
  "mt-1 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900";

export default function ExpenseForm({ form, setForm, categories, deals = [], onSubmit }) {
  const isMileage = form.category === "Mileage";
  const milesNum = parseFloat(form.miles) || 0;
  const estimatedMileage = milesNum > 0 ? milesNum * IRS_RATE_2026 : 0;

  return (
    <form onSubmit={onSubmit} className="border border-neutral-200 p-4 mb-6" data-testid="expense-quick-form">
      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        <div className="md:col-span-1">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Date</Label>
          <Input
            type="date"
            required
            value={form.expense_date}
            onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
            className={inputCls}
            data-testid="expense-date-input"
          />
        </div>
        <div className="md:col-span-1">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, miles: "" })}>
            <SelectTrigger className="mt-1 rounded-none border-neutral-300 focus:ring-0" data-testid="expense-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {categories.map((c) => (
                <SelectItem key={c} value={c} className="rounded-none">{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {isMileage ? (
          <div className="md:col-span-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1">
              <Car className="h-3 w-3" strokeWidth={1.5} /> Miles
            </Label>
            <Input
              type="number"
              step="0.1"
              value={form.miles}
              onChange={(e) => setForm({ ...form, miles: e.target.value })}
              className={`${inputCls} font-mono`}
              data-testid="expense-miles-input"
            />
          </div>
        ) : (
          <div className="md:col-span-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Amount</Label>
            <Input
              type="number"
              step="0.01"
              required
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className={`${inputCls} font-mono`}
              data-testid="expense-amount-input"
            />
          </div>
        )}
        <div className="md:col-span-1">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1">
            <Link2 className="h-3 w-3" strokeWidth={1.5} /> Deal (optional)
          </Label>
          <Select
            value={form.deal_id || "__none__"}
            onValueChange={(v) => setForm({ ...form, deal_id: v === "__none__" ? "" : v })}
          >
            <SelectTrigger className="mt-1 rounded-none border-neutral-300 focus:ring-0" data-testid="expense-deal-select">
              <SelectValue placeholder="No deal" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="__none__" className="rounded-none">— None —</SelectItem>
              {deals.map((d) => (
                <SelectItem key={d.id} value={d.id} className="rounded-none">{d.property_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-1">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Vendor</Label>
          <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className={inputCls} data-testid="expense-vendor-input" />
        </div>
        <div className="md:col-span-1">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Notes</Label>
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} data-testid="expense-notes-input" />
        </div>
        <div className="md:col-span-1 flex items-end">
          <Button type="submit" className="w-full rounded-none bg-neutral-900 hover:bg-neutral-800 text-white h-10" data-testid="expense-submit-button">
            <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} />
            Log
          </Button>
        </div>
      </div>
      {isMileage && milesNum > 0 && (
        <div className="mt-3 font-mono text-xs text-neutral-600 border-t border-neutral-200 pt-3" data-testid="mileage-preview">
          {milesNum} miles × <span className="text-neutral-900">${IRS_RATE_2026.toFixed(2)}/mile</span> (IRS 2026 rate) ={" "}
          <span className="font-semibold text-neutral-900">{formatUSD(estimatedMileage)}</span>
        </div>
      )}
    </form>
  );
}
